import { ehRiscoDeTanque } from './encaixeRiscos'
import { SITUACOES_FONTE, TIPOS_OBJETO_TABLETOP, type CenaTabletop, type ObjetoTabletop } from './tipos'
import { copiarCenaTabletop } from './operacoes'

const tiposConhecidos = new Set<string>(TIPOS_OBJETO_TABLETOP)

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

const ehStringNaoVazia = (valor: unknown): valor is string =>
  typeof valor === 'string' && valor.trim().length > 0

const ehVetorFinito = (valor: unknown): valor is readonly [number, number, number] =>
  Array.isArray(valor) &&
  valor.length === 3 &&
  valor.every((coordenada) => typeof coordenada === 'number' && Number.isFinite(coordenada))

const ehEscalaPositiva = (valor: unknown): valor is readonly [number, number, number] =>
  ehVetorFinito(valor) && valor.every((componente) => componente > 0)

const ehBlocoFonteValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.idProduto) &&
  typeof valor.enchimento === 'number' &&
  Number.isFinite(valor.enchimento) &&
  valor.enchimento >= 0 &&
  valor.enchimento <= 1 &&
  (valor.situacao === undefined || (typeof valor.situacao === 'string' && (SITUACOES_FONTE as readonly string[]).includes(valor.situacao))) &&
  (valor.vazaoKgS === undefined || (typeof valor.vazaoKgS === 'number' && Number.isFinite(valor.vazaoKgS) && valor.vazaoKgS >= 0))

const ehNumeroFinito = (valor: unknown): valor is number => typeof valor === 'number' && Number.isFinite(valor)

const ehMeteorologiaValida = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehNumeroFinito(valor.direcaoVentoGraus) &&
  ehNumeroFinito(valor.ventoMS) &&
  valor.ventoMS > 0 &&
  (valor.periodo === 'dia' || valor.periodo === 'noite') &&
  typeof valor.ceuEncoberto === 'boolean' &&
  (valor.terreno === 'aberto' || valor.terreno === 'urbano')

const ehObjetoTabletop = (valor: unknown): valor is ObjetoTabletop => {
  if (!ehRegistro(valor) || !ehRegistro(valor.transformacao)) return false
  const base =
    ehStringNaoVazia(valor.id) &&
    ehStringNaoVazia(valor.nome) &&
    typeof valor.tipo === 'string' &&
    tiposConhecidos.has(valor.tipo) &&
    (valor.idTanque === undefined || (ehStringNaoVazia(valor.idTanque) && ehRiscoDeTanque(valor.tipo as ObjetoTabletop['tipo']))) &&
    (valor.cor === undefined || typeof valor.cor === 'string') &&
    (valor.bloqueado === undefined || typeof valor.bloqueado === 'boolean') &&
    (valor.oculto === undefined || typeof valor.oculto === 'boolean') &&
    ehVetorFinito(valor.transformacao.posicao) &&
    ehVetorFinito(valor.transformacao.rotacao) &&
    ehEscalaPositiva(valor.transformacao.escala)
  if (!base) return false
  if (valor.fonte !== undefined && !ehBlocoFonteValido(valor.fonte)) return false
  return true
}

const ehItemAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.texto === 'string' &&
  typeof valor.confirmado === 'boolean' &&
  typeof valor.visivel === 'boolean'

const ehAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  typeof valor.natureza === 'string' &&
  typeof valor.local === 'string' &&
  typeof valor.horario === 'string' &&
  typeof valor.recursos === 'string' &&
  Array.isArray(valor.informacoes) &&
  valor.informacoes.every(ehItemAcionamentoValido)

export function validarCenaTabletop(valor: unknown): valor is CenaTabletop {
  if (
    !ehRegistro(valor) ||
    valor.versao !== 1 ||
    typeof valor.nome !== 'string' ||
    (valor.historiaInicial !== undefined && typeof valor.historiaInicial !== 'string') ||
    !Array.isArray(valor.objetos) ||
    !valor.objetos.every(ehObjetoTabletop) ||
    (valor.acionamento !== undefined && !ehAcionamentoValido(valor.acionamento)) ||
    (valor.meteorologia !== undefined && !ehMeteorologiaValida(valor.meteorologia))
  ) {
    return false
  }

  const objetos = valor.objetos as ObjetoTabletop[]
  if (objetos.some(o => o.idTanque && !objetos.some(t => t.id === o.idTanque && t.tipo === 'caminhaoTanque'))) return false
  const ids = valor.objetos.map((objeto) => objeto.id)
  return new Set(ids).size === ids.length
}

export function serializarCenaTabletop(cena: CenaTabletop): string {
  if (!validarCenaTabletop(cena)) {
    throw new Error('Arquivo de table top inválido: a cena não pode ser serializada.')
  }
  return JSON.stringify(cena, null, 2)
}

export function desserializarCenaTabletop(json: string): CenaTabletop {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Arquivo de table top inválido: o conteúdo não é um JSON válido.')
  }

  if (!validarCenaTabletop(valor)) {
    throw new Error('Arquivo de table top inválido: estrutura ou valores não reconhecidos.')
  }

  return copiarCenaTabletop(valor)
}
